// Case collaboration DTOs (coo-lab Domain A): internal comments, per-case chat, doctor/clinic
// change-requests, and treatment-plan entries. Read shapes + zod request schemas, imported by both
// the server (validation/persistence) and the client (forms + typed reads).
import { z } from 'zod';

// --- Comments ---------------------------------------------------------------

export interface CaseComment {
  id: string;
  caseId: string;
  authorUserId: string | null;
  authorName: string | null; // denormalized on read for display
  body: string;
  createdAt: string;
}

export const createCommentSchema = z.object({
  body: z.string().trim().min(1).max(4000),
});
export type CreateCommentRequest = z.infer<typeof createCommentSchema>;

// --- Chat messages ----------------------------------------------------------

export const CASE_MESSAGE_KINDS = ['text', 'file'] as const;
export type CaseMessageKind = (typeof CASE_MESSAGE_KINDS)[number];

export interface CaseMessage {
  id: string;
  caseId: string;
  authorUserId: string | null;
  authorName: string | null;
  kind: CaseMessageKind;
  body: string | null;
  originalName: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  createdAt: string;
}

export const createMessageSchema = z.object({
  body: z.string().trim().min(1).max(4000),
});
export type CreateMessageRequest = z.infer<typeof createMessageSchema>;

// --- Change / cancellation requests -----------------------------------------

export const CASE_REQUEST_KINDS = ['change', 'cancel'] as const;
export type CaseRequestKind = (typeof CASE_REQUEST_KINDS)[number];
export const CASE_REQUEST_KIND_LABELS: Record<CaseRequestKind, string> = {
  change: 'Change request',
  cancel: 'Cancellation',
};

export const CASE_REQUEST_STATUSES = ['pending', 'approved', 'rejected'] as const;
export type CaseRequestStatus = (typeof CASE_REQUEST_STATUSES)[number];
export const CASE_REQUEST_STATUS_LABELS: Record<CaseRequestStatus, string> = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
};

export interface CaseRequest {
  id: string;
  caseId: string;
  kind: CaseRequestKind;
  requestType: string | null;
  description: string;
  status: CaseRequestStatus;
  requestedByUserId: string | null;
  requestedByName: string | null;
  reviewNote: string | null;
  reviewedByUserId: string | null;
  reviewedAt: string | null;
  createdAt: string;
}

export const createRequestSchema = z.object({
  kind: z.enum(CASE_REQUEST_KINDS),
  requestType: z.string().trim().max(120).nullish(),
  description: z.string().trim().min(1).max(2000),
});
export type CreateRequestRequest = z.infer<typeof createRequestSchema>;

export const reviewRequestSchema = z.object({
  status: z.enum(['approved', 'rejected']),
  reviewNote: z.string().trim().max(1000).nullish(),
});
export type ReviewRequestRequest = z.infer<typeof reviewRequestSchema>;

// --- Treatment plans --------------------------------------------------------

export interface CaseTreatmentPlan {
  id: string;
  caseId: string;
  planNo: number;
  title: string | null;
  notes: string | null;
  mediaId: string | null;
  createdByUserId: string | null;
  createdAt: string;
}

export const createTreatmentPlanSchema = z.object({
  title: z.string().trim().max(160).nullish(),
  notes: z.string().trim().max(2000).nullish(),
  mediaId: z.string().min(1).nullish(),
});
export type CreateTreatmentPlanRequest = z.infer<typeof createTreatmentPlanSchema>;
